import usePermissions from "hooks/usePermissions";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import apiLibrary from "services/api";
import { Toasts } from "view/components/Toasts";

interface Props {
  vectorLayerType?: string;
  entityType: EntityType;
  currentOpenToggle: EntityType;
  setCurrentOpenToggle: Dispatch<SetStateAction<EntityType>>;
  page: Page;
  includeSnappingGeographies?: boolean;
}

export type EntityType = null | "vector" | "publicVector";
export type Page = {
  vector: number;
  publicVector: number;
  region?: number;
  shape?: number;
};

const DATA_LIMIT = 10;

const normalizeSnappingGeographies = (response: any) => {
  const result = response?.data ?? response;
  const items =
    (Array.isArray(result) ? result : undefined) ??
    result?.data ??
    result?.geographies ??
    result?.items ??
    [];

  return {
    data: Array.isArray(items) ? items : [],
    totalPages:
      response?.totalPages ??
      response?.total_pages ??
      result?.totalPages ??
      result?.total_pages ??
      1,
  };
};

const useSnappingGeographies = ({
  enabled,
  communityId,
  type,
  page,
}: {
  enabled: boolean;
  communityId?: string;
  type: "region" | "shape";
  page: number;
}) => {
  const [result, setResult] = useState<{ data: any[]; totalPages: number }>({
    data: [],
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !communityId) return;

    setLoading(true);
    apiLibrary.geography
      .getSnappingGeographies(communityId, {
        type,
        limit: DATA_LIMIT,
        page,
      })
      .then((response: any) => {
        const nextResult = normalizeSnappingGeographies(response);
        setResult((previous) => ({
          totalPages: nextResult.totalPages,
          data:
            page === 1
              ? nextResult.data
              : [
                  ...previous.data,
                  ...nextResult.data.filter(
                    (item: any) =>
                      !previous.data.some(
                        (existing: any) => existing.id === item.id,
                      ),
                  ),
                ],
        }));
      })
      .catch((error: any) =>
        Toasts.error(error?.response?.data?.message ?? error.message),
      )
      .finally(() => setLoading(false));
  }, [enabled, communityId, type, page]);

  return { result, loading };
};

const useFetchGlobalAndCommunityVectorLayers = ({
  vectorLayerType,
  entityType,
  currentOpenToggle,
  setCurrentOpenToggle,
  page,
  includeSnappingGeographies = false,
}: Props) => {
  const [communityLayersLoading, setCommunityLayersLoading] = useState(false);
  const [globalLayersLoading, setGlobalLayersLoading] = useState(false);
  const [communityVectorLayers, setCommunityVectorLayers] = useState({
    vector: [],
    totalPages: 1,
  });
  const [globalVectorLayers, setGlobaVectorLayers] = useState({
    vector: [],
    totalPages: 1,
  });
  const { communityId } = useParams();
  const { communities } = usePermissions();
  const canFetchSnappingGeographies = Boolean(
    includeSnappingGeographies &&
      communityId &&
      communities.canViewVectorLayersCommunities,
  );
  const { result: communityRegions, loading: communityRegionsLoading } =
    useSnappingGeographies({
      enabled: canFetchSnappingGeographies,
      communityId,
      type: "region",
      page: page.region ?? 1,
    });
  const { result: communityShapes, loading: communityShapesLoading } =
    useSnappingGeographies({
      enabled: canFetchSnappingGeographies,
      communityId,
      type: "shape",
      page: page.shape ?? 1,
    });

  useEffect(() => {
    const params = (type: string) => ({
      entityType: type,
      limit: DATA_LIMIT,
      page: 1,
    });
    // fetching community layers
    if (communityId && communities.canViewVectorLayersCommunities) {
      setCommunityLayersLoading(true);
      apiLibrary.geography
        .getVectorLayersByCommunityId(
          communityId,
          vectorLayerType,
          params("vector"),
        )
        .then(({ data }: any) => {
          setCommunityVectorLayers(data);

          if (data.vector.length > 0) {
            if (!currentOpenToggle) setCurrentOpenToggle("vector");
          }
        })
        .catch((error: any) => Toasts.error(error.message))
        .finally(() => setCommunityLayersLoading(false));
    }

    // fetching global layers
    setGlobalLayersLoading(true);

    apiLibrary.geography
      .getGlobalVectorLayers(vectorLayerType, params("publicVector"))
      .then(({ data }: any) => {
        setGlobaVectorLayers(data);

        if (data.vector.length > 0) {
          if (!currentOpenToggle) setCurrentOpenToggle("publicVector");
        }
      })
      .catch((error: any) => Toasts.error(error.message))
      .finally(() => setGlobalLayersLoading(false));
  }, []);

  useEffect(() => {
    if (!entityType) return;

    if (
      (entityType === "vector" &&
        communityVectorLayers?.totalPages < page?.vector) ||
      (entityType === "publicVector" &&
        globalVectorLayers?.totalPages < page?.publicVector)
    ) {
      return;
    }

    const params = {
      limit: DATA_LIMIT,
      page: page[entityType ?? ""],
    };

    const fetchLayers =
      entityType === "vector" &&
      communityId &&
      communities.canViewVectorLayersCommunities
        ? apiLibrary.geography.getVectorLayersByCommunityId(
            communityId,
            vectorLayerType,
            params,
          )
        : apiLibrary.geography.getGlobalVectorLayers(vectorLayerType, params);

    entityType === "vector"
      ? setCommunityLayersLoading(true)
      : setGlobalLayersLoading(true);

    fetchLayers
      .then(({ data }) => {
        if (entityType === "vector") {
          setCommunityVectorLayers((prevLayers: any) => ({
            ...prevLayers,
            vector: [...prevLayers.vector, ...data.vector],
          }));
        } else {
          setGlobaVectorLayers((prevLayers: any) => ({
            ...prevLayers,
            vector: [...prevLayers.vector, ...data.vector],
          }));
        }
      })
      .catch((err) => {
        Toasts.error(err.message);
      })
      .finally(() => {
        entityType === "vector"
          ? setCommunityLayersLoading(false)
          : setGlobalLayersLoading(false);
      });
  }, [page]);

  return {
    communityLayersLoading,
    globalLayersLoading,
    communityVectorLayers,
    globalVectorLayers,
    communityRegions,
    communityShapes,
    communityRegionsLoading,
    communityShapesLoading,
  };
};

export { useFetchGlobalAndCommunityVectorLayers };
